var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
import * as A from "fp-ts/Array";
import { flow, identity, pipe } from "fp-ts/function";
import { not } from "fp-ts/Predicate";
import * as T from "fp-ts/Tuple";
import { Node_Type } from "./../../ricos-schema/legacy/wix/rich_content/v1";
import { and } from "../fp-utils";
import { extract } from "../RicosContentAPI/extract";
import { modify } from "../RicosContentAPI/modify";
import { fromEntries } from "../utils";
import { getFilteredNodes } from "../visitNodes/index";
import { DEFAULT_COUNTABLE_TYPES, isNonTextual, isTextContainer as isTextual } from "./types";
const isNonTextualNode = (node) => isNonTextual(node.type);
const isTextualNode = (node) => isTextual(node.type);
const isEmptyParagraph = (node) => {
  var _a;
  return node.type === Node_Type.PARAGRAPH && ((_a = node.nodes) == null ? void 0 : _a.length) === 0;
};
const isTextNode = (node) => node.type === Node_Type.TEXT;
const toId = (node) => node.id;
const toWords = (node) => extract(node).map(({ textData }) => {
  var _a;
  return (_a = textData == null ? void 0 : textData.text) != null ? _a : "";
}).get().flat().join("").split(" ").filter((w) => w !== "");
const toTextLength = (node) => extract(node).map(({ textData }) => {
  var _a, _b;
  return (_b = (_a = textData == null ? void 0 : textData.text) == null ? void 0 : _a.length) != null ? _b : 0;
}).get().flat().reduce((acc, l) => acc + l, 0);
const toWordCount = flow(toWords, A.size);
const toNodeWordCount = (node) => pipe([node, node], T.bimap(toWordCount, toId));
const limitToCount = (count) => (nodeWordCount) => nodeWordCount.reduce(
  ([acc, wordLimit], [id, nodeCount]) => [
    [...acc, [id, Math.min(wordLimit, nodeCount)]],
    Math.max(wordLimit - nodeCount, 0)
  ],
  [[], count]
)[0];
const limitWords = (node, count) => {
  var _a, _b, _c, _d;
  const wordCount = toWordCount(node);
  if (wordCount === count) {
    return node;
  }
  const truncatedLength = toWords(node).slice(0, count).join(" ").length;
  const nodeTextLengths = (_b = (_a = node.nodes) == null ? void 0 : _a.filter(isTextNode).map(toTextLength)) != null ? _b : [];
  const reducedLengths = nodeTextLengths.reduce(
    ([reducedLengths2, limit], nodeLength) => [
      [...reducedLengths2, Math.min(nodeLength, limit)],
      Math.max(limit - nodeLength, 0)
    ],
    [[], truncatedLength]
  )[0];
  const shouldAppendEllipsis = (i) => reducedLengths[i] > 0 && reducedLengths[i] < nodeTextLengths[i] || reducedLengths[i] > 0 && reducedLengths[i + 1] === 0;
  const nodes = (_d = (_c = node.nodes) == null ? void 0 : _c.filter(isTextNode).map((n, i) => {
    var _a2, _b2, _c2, _d2, _e;
    return __spreadProps(__spreadValues({}, n), {
      textData: __spreadProps(__spreadValues({}, n.textData), {
        decorations: (_b2 = (_a2 = n.textData) == null ? void 0 : _a2.decorations) != null ? _b2 : [],
        text: (_e = (_d2 = (_c2 = n.textData) == null ? void 0 : _c2.text) == null ? void 0 : _d2.slice(0, reducedLengths[i]).concat(shouldAppendEllipsis(i) ? "…" : "")) != null ? _e : ""
      })
    });
  }).filter((n) => {
    var _a2, _b2;
    return (_b2 = (_a2 = n.textData) == null ? void 0 : _a2.text) == null ? void 0 : _b2.length;
  })) != null ? _d : [];
  return __spreadProps(__spreadValues({}, node), { nodes });
};
const defaultSelectCountableNodes = (node) => !isEmptyParagraph(node) && DEFAULT_COUNTABLE_TYPES.includes(node.type);
const getCountableNodes = getFilteredNodes;
const applyToContent = (content) => (nodeWordCount) => modify(content).filter((n) => Object.keys(nodeWordCount).includes(n.id)).set((n) => nodeWordCount[n.id] === 0 ? [] : [limitWords(n, nodeWordCount[n.id])]);
const byNodeCount = (count, selectCountableNodes) => (content) => pipe(
  content,
  getCountableNodes(selectCountableNodes),
  (nodes) => nodes.slice(count).map(toId),
  removeNodesById(content)
);
const byWordsCount = (count) => (content) => {
  var _a;
  return pipe(
    extract((_a = content.nodes) != null ? _a : []).filter(and([isTextualNode, not(isEmptyParagraph)])).get(),
    A.map(toNodeWordCount),
    limitToCount(count),
    fromEntries,
    applyToContent(content)
  );
};
const removeNodesById = (content) => (ids) => modify(content).set((n) => ids.includes(n.id) ? [] : [n]);
const byNonTextNodesCount = (count) => (content) => {
  var _a;
  return pipe(
    extract((_a = content == null ? void 0 : content.nodes) != null ? _a : []).filter(isNonTextualNode).map(toId).get().slice(count),
    removeNodesById(content)
  );
};
const withFallback = (limits) => __spreadValues(__spreadValues({}, {
  nodeCount: Infinity,
  wordCount: Infinity,
  maxPlugins: Infinity
}), limits);
const removeEmptyContainers = (content) => modify(content).filter(
  (n) => [
    Node_Type.LIST_ITEM,
    Node_Type.ORDERED_LIST,
    Node_Type.BULLETED_LIST,
    Node_Type.BLOCKQUOTE,
    Node_Type.TABLE,
    Node_Type.COLLAPSIBLE_LIST
  ].includes(n.type)
).set((n) => {
  var _a;
  return ((_a = n.nodes) == null ? void 0 : _a.length) === 0 ? [] : [n];
});
const removeEmptyRows = (content) => modify(content).filter((n) => [Node_Type.TABLE_ROW, Node_Type.COLLAPSIBLE_ITEM].includes(n.type)).set((n) => {
  var _a;
  return ((_a = n.nodes) == null ? void 0 : _a.every((cell) => {
    var _a2;
    return ((_a2 = cell.nodes) == null ? void 0 : _a2.length) === 0;
  })) ? [] : [n];
});
const mergeEmptyParagraphs = (content) => pipe(
  content,
  getFilteredNodes(
    (node, { previousSibling }) => !!(isEmptyParagraph(node) && previousSibling && isEmptyParagraph(previousSibling))
  ),
  (nodes) => nodes.map(toId),
  removeNodesById(content)
);
const truncateRichContent = (content, limits, options) => {
  const { nodeCount, wordCount, maxPlugins } = withFallback(limits);
  return pipe(
    content,
    byNonTextNodesCount(maxPlugins),
    byNodeCount(nodeCount, (options == null ? void 0 : options.selectCountableNodes) || defaultSelectCountableNodes),
    byWordsCount(wordCount),
    removeEmptyRows,
    removeEmptyContainers,
    (options == null ? void 0 : options.keepEmptyParagraphs) ? identity : mergeEmptyParagraphs
  );
};
export {
  getCountableNodes,
  truncateRichContent
};
//# sourceMappingURL=ricos-truncate.js.map
